import { CompleteGlbArchiveDecompressor } from './CompleteGlbArchiveDecompressor'

/** Worker 执行期间检查请求是否仍有效的间隔。 */
const STALE_REQUEST_CHECK_INTERVAL_MS = 25

/**
 * complete GLB ZIP 的浏览器 Worker 协调器。
 *
 * 每次解压使用一个独立 Worker，完成、失败或请求过期后都会立即终止并解除监听，
 * 因而 stored/低压缩 entry 也不会占用 UI 主线程。
 */
export class CompleteGlbPackageWorkerClient implements PuppyCADWeb.CompleteGlbPackageDecompressor {
  /** 创建 Worker 的可选工厂；非浏览器环境为空并回退到同一解压执行器。 */
  private readonly workerFactory?: PuppyCADWeb.CompleteGlbPackageWorkerFactory

  /**
   * 创建解压 Worker 协调器。
   *
   * @param workerFactory 可选 Worker 工厂；测试或 SSR 环境可显式传入。
   */
  public constructor(workerFactory?: PuppyCADWeb.CompleteGlbPackageWorkerFactory) {
    this.workerFactory = workerFactory
  }

  /**
   * 在浏览器专用 Worker 中解压归档；非浏览器环境使用同一执行器回退。
   *
   * @param input ZIP 字节、资源限制与请求断言。
   * @returns 解压后的 entry 映射。
   */
  public async decompress(
    input: PuppyCADWeb.CompleteGlbPackageDecompressInput,
  ): Promise<Record<string, Uint8Array>> {
    input.assertCurrent?.()
    if (!this.workerFactory && typeof Worker === 'undefined') {
      return new CompleteGlbArchiveDecompressor().decompress(input)
    }

    // 版本文件字节仍归项目缓存所有，不能直接 transfer 导致调用方缓存被 detach。
    const workerBytes = new Uint8Array(input.bytes)
    const worker = this.workerFactory
      ? this.workerFactory(
          new URL('./complete-glb-package.worker.ts', import.meta.url),
          { type: 'module', name: 'complete-glb-package-unzip' },
        )
      // Vite 需要直接识别 new Worker(new URL(...)) 语法，才能把 Worker 入口独立打包。
      : new Worker(
          new URL('./complete-glb-package.worker.ts', import.meta.url),
          { type: 'module', name: 'complete-glb-package-unzip' },
        )

    return await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
      let settled = false
      let staleCheckTimer: ReturnType<typeof setInterval> | undefined

      /** 释放一次性 Worker 的全部资源，并只交付一次结果。 */
      const settle = (
        action: () => void,
      ): void => {
        if (settled) {
          return
        }
        settled = true
        if (staleCheckTimer !== undefined) {
          clearInterval(staleCheckTimer)
        }
        worker.removeEventListener('message', handleMessage)
        worker.removeEventListener('error', handleError)
        worker.terminate()
        action()
      }

      /**
       * 处理 Worker 返回的完整归档解压结果，并在交付前再次确认请求仍有效。
       *
       * @param event Worker 成功响应或结构化错误响应。
       */
      const handleMessage = (
        event: MessageEvent<PuppyCADWeb.CompleteGlbPackageWorkerResponse>,
      ): void => {
        try {
          input.assertCurrent?.()
          const response = event.data
          if (response.kind === 'error') {
            const message = response.message
            settle(() => reject(new Error(message)))
            return
          }
          const files = response.files
          settle(() => resolve(files))
        } catch (error) {
          settle(() => reject(error))
        }
      }
      /**
       * 将 Worker 运行时错误转换为带完整 GLB 上下文的异常并释放资源。
       *
       * @param event 浏览器 Worker 派发的错误事件。
       */
      const handleError = (event: ErrorEvent): void => {
        settle(() => reject(new Error(
          `完整 GLB 解压 Worker 执行失败：${event.message || 'unknown worker error'}`,
        )))
      }
      worker.addEventListener('message', handleMessage)
      worker.addEventListener('error', handleError)

      if (input.assertCurrent) {
        staleCheckTimer = setInterval(() => {
          try {
            input.assertCurrent?.()
          } catch (error) {
            // 请求一旦失效就立即 terminate，避免后台继续消耗 CPU 与内存。
            settle(() => reject(error))
          }
        }, STALE_REQUEST_CHECK_INTERVAL_MS)
      }

      try {
        const request: PuppyCADWeb.CompleteGlbPackageWorkerRequest = {
          bytes: workerBytes,
          limits: input.limits,
        }
        worker.postMessage(request, [workerBytes.buffer])
      } catch (error) {
        settle(() => reject(error))
      }
    })
  }
}
